import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Onboarding.css';
import ChaiMateLogo from '../images/chaimate-logo.svg';
import { apiCall } from '../utils/api';

// Speech Recognition setup
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

// Voice input button component
const VoiceInputButton = ({ isListening, onClick, disabled }) => (
    <button 
        className={`voice-input-btn ${isListening ? 'listening' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={isListening ? "Listening..." : "Speak to answer"}
    >
        <div className="voice-btn-content">
            {isListening ? (
                <>
                    <div className="listening-waves">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <span className="voice-btn-text">Sun raha hoon...</span>
                </>
            ) : (
                <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" y1="19" x2="12" y2="23"/>
                        <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                    <span className="voice-btn-text">Bolke batao</span>
                </>
            )}
        </div>
    </button>
);

// Audio controls component
const AudioControls = ({ onReplay, isPlaying }) => (
    <div className={`audio-controls ${isPlaying ? 'playing' : ''}`}>
        <button 
            className={`audio-btn ${isPlaying ? 'playing' : ''}`}
            onClick={onReplay}
            aria-label={isPlaying ? "Playing" : "Replay voice"}
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
            </svg>
        </button>
        <button 
            className="audio-btn replay-btn"
            onClick={onReplay}
            aria-label="Replay"
        >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 4v6h6"/>
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
        </button>
    </div>
);

// Progress dots component
const ProgressDots = ({ current, total }) => (
    <div className="progress-dots">
        {Array.from({ length: total }, (_, i) => (
            <div 
                key={i} 
                className={`progress-dot ${i < current ? 'completed' : ''} ${i === current ? 'active' : ''}`}
            />
        ))}
    </div>
);

// Language options - limited to English, Hindi, Gujarati
const LANGUAGE_OPTIONS = [
    { value: 'english', label: 'English', native: 'English' },
    { value: 'hindi', label: 'Hindi', native: 'हिंदी' },
    { value: 'gujarati', label: 'Gujarati', native: 'ગુજરાતી' },
];

// Experience options
const EXPERIENCE_OPTIONS = [
    { value: 0, label: 'Fresher', sublabel: 'Abhi shuru kiya' },
    { value: 1, label: '1 year', sublabel: '1 saal' },
    { value: 2, label: '2 years', sublabel: '2 saal' },
    { value: 3, label: '3 years', sublabel: '3 saal' },
    { value: 4.5, label: '4-5 years', sublabel: '4-5 saal' },
    { value: 8, label: '6-10 years', sublabel: '6-10 saal' },
    { value: 10, label: '10+ years', sublabel: '10+ saal' },
];

// Role options (first question after city)
// CA/CS Status options (shown after selecting CA or CS)
const CA_CS_STATUS_OPTIONS = [
    { value: 'foundation', label: 'Foundation', sublabel: 'Shuruaati level' },
    { value: 'intermediate', label: 'Intermediate', sublabel: 'Intermediate exams' },
    { value: 'articleship', label: 'Articleship', sublabel: 'Articleship period' },
    { value: 'finals', label: 'Finals', sublabel: 'Final exams phase' },
    { value: 'cleared', label: 'Clear kar liya hai', sublabel: 'Qualification complete' },
];

const CA_COURSE_LEVELS_WITH_COACHING = ['foundation', 'intermediate'];
const CA_COURSE_LEVELS_WITH_ARTICLESHIP_LOCATION = ['articleship', 'finals'];
const CA_COURSE_LEVELS_WITH_WORKPLACE = ['cleared'];

// Qualification options (for students)
const QUALIFICATION_OPTIONS = [
    'B.Com', 'M.Com', 'CA Inter', 'CA', 'CMA Inter', 'CMA', 'CS Inter', 'CS'
];

// City options limited to Gujarat
const CITY_OPTIONS = [
    'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Gandhinagar',
    'Jamnagar', 'Junagadh', 'Anand', 'Mehsana', 'Porbandar', 'Nadiad',
    'Surendranagar', 'Navsari', 'Valsad'
];

// Goal options
const GOAL_OPTIONS = [
    { id: 'salary', label: 'Increase salary', sublabel: 'Salary badhana hai', emoji: '💰' },
    { id: 'exams', label: 'Clear exams', sublabel: 'Exams clear karne hai', emoji: '📚' },
    { id: 'switch', label: 'Switch role/company', sublabel: 'Role ya company change karni hai', emoji: '🔄' },
    { id: 'network', label: 'Build network', sublabel: 'Network banana hai', emoji: '🤝' },
    { id: 'skills', label: 'Learn new skills', sublabel: 'Naye skills seekhne hai', emoji: '🎯' },
    { id: 'business', label: 'Start own practice', sublabel: 'Apna practice shuru karna hai', emoji: '🏢' },
];

// Voice narration texts
const NARRATIONS = {
    language: "Aapki pasandida language kya hai?",
    name: "Aapka naam kya hai?",
    birthday: "Aapka birthday kab aata hai?",
    city: "Aap kis sheher mein ho?",
    ca_cs_status: "Aap kis CA course level par ho?",
    coaching_institute: "Aap kis coaching institute se padhai kar rahe ho?",
    articleship_location: "Aapne kaha articleship ki hai?",
    organisation_name: "Aap kis organisation mein kaam karte ho?",
    qualification: "Aapki qualification kya hai?",
    experience: "Aapko kitne saal ka experience hai?",
    current_role: "Aajkal kaha kaam karte ho?",
    goals: "Aapke goals kya hain?",
    loading: "Profile ready ho raha hai…"
};

const VOICE_URLS = {
    language: 'https://storage.googleapis.com/onboarding-deva/language.mp3',
    name: 'https://storage.googleapis.com/onboarding-deva/name.mp3',
    birthday: 'https://storage.googleapis.com/onboarding-deva/birthday.mp3',
    city: 'https://storage.googleapis.com/onboarding-deva/city.mp3',
    ca_cs_status: 'https://storage.googleapis.com/onboarding-deva/course_level.mp3',
    coaching_institute: 'https://storage.googleapis.com/onboarding-deva/coaching.mp3',
    articleship_location: 'https://storage.googleapis.com/onboarding-deva/articleship.mp3',
    organisation_name: 'https://storage.googleapis.com/onboarding-deva/organisation.mp3',
    qualification: 'https://storage.googleapis.com/onboarding-deva/qualification.mp3',
    experience: 'https://storage.googleapis.com/onboarding-deva/experience.mp3',
    current_role: 'https://storage.googleapis.com/onboarding-deva/role.mp3',
    goals: 'https://storage.googleapis.com/onboarding-deva/goals.mp3',
    loading: 'https://storage.googleapis.com/onboarding-deva/loading.mp3'
};

const MONTH_KEYWORDS = {
    january: 1, jan: 1, janv: 1, जनवरी: 1,
    february: 2, feb: 2, फ़रवरी: 2, फ़ेब: 2,
    march: 3, mar: 3, मार्च: 3,
    april: 4, apr: 4, एप्रिल: 4,
    may: 5, मई: 5,
    june: 6, jun: 6, जून: 6,
    july: 7, jul: 7, जुलाई: 7,
    august: 8, aug: 8, अगस्त: 8,
    september: 9, sept: 9, sep: 9, सितम्बर: 9, सितंबर: 9,
    october: 10, oct: 10, अक्टूबर: 10,
    november: 11, nov: 11, नवम्बर: 11,
    december: 12, dec: 12, दिसंबर: 12, दिसम्बर: 12,
};

const standardizeYear = (value) => {
    const parsed = Number(value);
    if (isNaN(parsed)) return null;
    if (parsed <= 31) return null;
    if (parsed >= 1000) return parsed;
    const currentYear = new Date().getFullYear();
    const pivot = currentYear % 100;
    if (parsed <= pivot) {
        return 2000 + parsed;
    }
    return 1900 + parsed;
};

const buildIsoDate = (year, month, day) => {
    if (!year || !month || !day) return null;
    const normalized = new Date(year, month - 1, day);
    if (isNaN(normalized.getTime())) return null;
    if (normalized.getDate() !== Number(day) || normalized.getMonth() + 1 !== Number(month)) {
        return null;
    }
    return normalized.toISOString().split('T')[0];
};

const parseSlashBirthday = (text) => {
    const match = text.match(/(\d{1,2})[\/\-\s](\d{1,2})[\/\-\s](\d{2,4})/);
    if (!match) return null;
    const [, rawDay, rawMonth, rawYear] = match;
    const year = standardizeYear(rawYear) || Number(rawYear);
    return buildIsoDate(year, Number(rawMonth), Number(rawDay));
};

const parseBirthdayFromText = (rawText) => {
    if (!rawText) return null;
    const lowercased = rawText.toLowerCase();
    const slashDate = parseSlashBirthday(lowercased);
    if (slashDate) return slashDate;
    
    // Strip ordinal suffixes (1st, 2nd, 3rd, 4th, 9th, 21st, etc.)
    const withoutOrdinals = lowercased.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    const normalized = withoutOrdinals.replace(/[.,]/g, ' ');
    const tokens = normalized.split(/\s+/).filter(Boolean);
    const numbers = [];
    let month = null;

    tokens.forEach(token => {
        if (!month && MONTH_KEYWORDS[token]) {
            month = MONTH_KEYWORDS[token];
        }
        const num = Number(token);
        if (!Number.isNaN(num)) {
            numbers.push(num);
        }
    });

    let day = null;
    let year = null;

    const yearIndex = numbers.findIndex(num => num > 31);
    if (yearIndex !== -1) {
        year = standardizeYear(numbers[yearIndex]) || numbers[yearIndex];
        numbers.splice(yearIndex, 1);
    }

    if (!year && numbers.length) {
        const last = numbers[numbers.length - 1];
        if (last <= 99) {
            year = standardizeYear(last);
            numbers.pop();
        }
    }

    if (!year && numbers.length) {
        const candidate = numbers.find(num => num > 31);
        if (candidate) {
            year = standardizeYear(candidate) || candidate;
            numbers.splice(numbers.indexOf(candidate), 1);
        }
    }

    if (!day && numbers.length) {
        day = numbers.shift();
    }

    if (!month && numbers.length) {
        const candidate = numbers.shift();
        if (candidate >= 1 && candidate <= 12) {
            month = candidate;
        }
    }

    if (!day && numbers.length) {
        day = numbers.shift();
    }

    if (!month && numbers.length) {
        const candidate = numbers.find(num => num >= 1 && num <= 12);
        if (candidate) {
            month = candidate;
        }
    }

    if (!year) {
        year = new Date().getFullYear();
    }

    return buildIsoDate(year, month, day);
};

const normalizeBirthdayValue = (value) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
    }
    return parseBirthdayFromText(trimmed) || '';
};

const formatBirthdayDisplay = (isoValue) => {
    if (!isoValue) return '';
    const parsed = new Date(isoValue);
    if (isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
};

const isValidBirthday = (value) => {
    if (!value) return false;
    const parsed = new Date(value);
    return !isNaN(parsed.getTime());
};

// LLM-based date parsing for more accurate voice transcription handling
const parseDateWithLLM = async (spokenText, isFutureDate = false) => {
    console.log('🎤 parseDateWithLLM called with:', { spokenText, isFutureDate });
    try {
        const response = await apiCall.post('/llm/parse-date', {
            text: spokenText,
            is_future_date: isFutureDate
        });
        
        console.log('📅 LLM parse-date API response:', response);
        
        const extractedDate = response?.date || response?.data?.date;
        if (extractedDate && /^\d{4}-\d{2}-\d{2}$/.test(extractedDate)) {
            console.log('✅ Valid date parsed:', extractedDate);
            return extractedDate;
        }
        console.log('⚠️ No valid date in response, returning null');
        return null;
    } catch (error) {
        console.error('❌ LLM date parsing failed, falling back to local parser:', error);
        // Fallback to local parsing if LLM call fails
        const fallbackResult = parseBirthdayFromText(spokenText);
        console.log('🔄 Local parser fallback result:', fallbackResult);
        return fallbackResult;
    }
};

const Onboarding = ({ onComplete, existingData = {} }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(false);
    const [heardText, setHeardText] = useState(''); // Show what was heard
    const [autoListenEnabled, setAutoListenEnabled] = useState(true); // Voice-first mode
    const [isAdvancing, setIsAdvancing] = useState(false); // Prevent double advances
    const speechRef = useRef(null);
    const audioRef = useRef(null);
    const recognitionRef = useRef(null);
    const autoListenTimeoutRef = useRef(null);
    const currentStepRef = useRef(currentStep); // Track current step for async handlers
    const lastSpokenStepRef = useRef(-1); // Track which step we last spoke for
    const startListeningRef = useRef(null); // Ref to hold startListening function
    
    // Keep ref in sync with state
    useEffect(() => {
        currentStepRef.current = currentStep;
    }, [currentStep]);
    
    // Form data - maps to profile fields
    const [formData, setFormData] = useState({
        language: existingData.language || '',
        name: existingData.name || '',
    birthday: normalizeBirthdayValue(existingData.birthday || ''),
    city: existingData.city || '',
    ca_cs_status: existingData.ca_cs_status || '', // course level for CA
        organisation_name: existingData.organisation_name || '', // workplace/org name when asked
        coaching_institute: existingData.coaching_institute || '',
        articleship_location: existingData.articleship_location || '',
        qualification: existingData.qualification || '',
        total_experience_years: existingData.total_experience_years ?? null,
        current_role: existingData.current_role || '',
        goals: existingData.goals || [],
    });
    const [cityOptionsState, setCityOptionsState] = useState(() => {
        const initialCities = existingData.city ? [existingData.city] : [];
        return Array.from(new Set([...CITY_OPTIONS, ...initialCities]));
    });

    // Check for speech recognition support
    useEffect(() => {
        setVoiceSupported(!!SpeechRecognition);
    }, []);

    // Build steps dynamically based on CA course level selection
    const getSteps = () => {
    const baseSteps = [
        { key: 'language', type: 'language', options: LANGUAGE_OPTIONS, required: true },
        { key: 'name', type: 'text', required: true },
        { key: 'birthday', type: 'birthday', required: true },
        { key: 'city', type: 'chips', options: cityOptionsState, required: true },
        { key: 'ca_cs_status', type: 'ca_cs_status', options: CA_CS_STATUS_OPTIONS, required: true },
    ];

    if (formData.ca_cs_status) {
        if (CA_COURSE_LEVELS_WITH_COACHING.includes(formData.ca_cs_status)) {
            baseSteps.push({ key: 'coaching_institute', type: 'text', required: true });
        }

        if (CA_COURSE_LEVELS_WITH_ARTICLESHIP_LOCATION.includes(formData.ca_cs_status)) {
            baseSteps.push({ key: 'articleship_location', type: 'text', required: true });
        }

        if (CA_COURSE_LEVELS_WITH_WORKPLACE.includes(formData.ca_cs_status)) {
            baseSteps.push({ key: 'organisation_name', type: 'text', required: true });
            baseSteps.push({ key: 'experience', type: 'experience', required: true });
        }
    }

    baseSteps.push(
        { key: 'goals', type: 'multiselect', options: GOAL_OPTIONS, required: true },
    );
        
        return baseSteps;
    };
    
    const STEPS = getSteps();

    const currentStepConfig = STEPS[currentStep];
    const totalSteps = STEPS.length;

    const stopAllAudio = useCallback(() => {
        try {
            if ('speechSynthesis' in window) window.speechSynthesis.cancel();
        } catch (e) {
            // ignore
        }
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current = null;
            }
        } catch (e) {
            // ignore
        }
    }, []);

    // Play static GCS-hosted voice for a given step key, with Web Speech fallback.
    const speak = useCallback(async (stepKey, text, shouldAutoListen = true) => {
        // Stop any previous audio
        stopAllAudio();
        setIsPlaying(false);
        setHeardText(''); // Clear previous heard text

        const voiceUrl = VOICE_URLS[stepKey];
        if (voiceUrl) {
            const audio = new Audio(voiceUrl);
            audioRef.current = audio;

            // Sync animation with actual audio playback
            audio.onplay = () => setIsPlaying(true);
            audio.onplaying = () => setIsPlaying(true);
            audio.onended = () => {
                setIsPlaying(false);
                // Auto-start listening after audio ends (voice-first mode)
                // Note: startListeningInternal will check isAdvancing itself
                if (shouldAutoListen && autoListenEnabled && SpeechRecognition && stepKey !== 'loading') {
                    autoListenTimeoutRef.current = setTimeout(() => {
                        if (startListeningRef.current) {
                            startListeningRef.current();
                        }
                    }, 500);
                }
            };
            audio.onerror = () => setIsPlaying(false);
            audio.onpause = () => setIsPlaying(false);

            try {
                await audio.play();
                return;
            } catch (e) {
                setIsPlaying(false);
                // If audio fails, still try to auto-listen
                if (shouldAutoListen && autoListenEnabled && SpeechRecognition && stepKey !== 'loading') {
                    autoListenTimeoutRef.current = setTimeout(() => {
                        if (startListeningRef.current) {
                            startListeningRef.current();
                        }
                    }, 500);
                }
            }
        }

        // Fallback to Web Speech API
        if ('speechSynthesis' in window) {
            try {
                window.speechSynthesis.cancel();
            } catch (e) {
                // ignore
            }

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'hi-IN';
            utterance.rate = 0.9;
            utterance.pitch = 1;

            utterance.onstart = () => setIsPlaying(true);
            utterance.onend = () => {
                setIsPlaying(false);
                // Auto-start listening after speech ends
                if (shouldAutoListen && autoListenEnabled && SpeechRecognition && stepKey !== 'loading') {
                    autoListenTimeoutRef.current = setTimeout(() => {
                        if (startListeningRef.current) {
                            startListeningRef.current();
                        }
                    }, 500);
                }
            };
            utterance.onerror = () => setIsPlaying(false);

            speechRef.current = utterance;
            try {
                window.speechSynthesis.speak(utterance);
                return;
            } catch (e) {
                setIsPlaying(false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopAllAudio, autoListenEnabled]);

    // Auto-narrate on step change (only once per step)
    useEffect(() => {
        // Prevent speaking same step twice
        if (lastSpokenStepRef.current === currentStep) {
            return;
        }
        
        const stepKey = STEPS[currentStep]?.key;
        if (stepKey && NARRATIONS[stepKey]) {
            lastSpokenStepRef.current = currentStep;
            // Small delay for smoother UX
            const timer = setTimeout(() => {
                speak(stepKey, NARRATIONS[stepKey]);
            }, 300);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    // Cleanup speech on unmount
    useEffect(() => {
        return () => {
            stopAllAudio();
        };
    }, [stopAllAudio]);

    const handleReplay = () => {
        const stepKey = STEPS[currentStep]?.key;
        if (stepKey && NARRATIONS[stepKey]) {
            speak(stepKey, NARRATIONS[stepKey]);
        }
    };

    const isStepValid = () => {
        const step = STEPS[currentStep];
        if (!step.required) return true;
        
        switch (step.key) {
            case 'language':
                return formData.language.length > 0;
            case 'name':
                return formData.name.trim().length >= 2;
            case 'birthday':
                return isValidBirthday(formData.birthday);
            case 'city':
                return formData.city.length > 0;
            case 'ca_cs_status':
                return formData.ca_cs_status.length > 0;
            case 'organisation_name':
                return formData.organisation_name.trim().length >= 2;
            case 'coaching_institute':
                return formData.coaching_institute.trim().length >= 2;
            case 'articleship_location':
                return formData.articleship_location.trim().length >= 2;
            case 'qualification':
                return formData.qualification.length > 0;
            case 'experience':
                return formData.total_experience_years !== null;
            case 'current_role':
                return true; // Optional
            case 'goals':
                return formData.goals.length > 0;
            default:
                return false;
        }
    };

    const handleNext = async () => {
        // Prevent double advances
        if (isAdvancing) return;
        
        // Stop any ongoing listening
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        if (autoListenTimeoutRef.current) {
            clearTimeout(autoListenTimeoutRef.current);
        }
        setIsListening(false);
        setHeardText('');
        
        if (currentStep < totalSteps - 1) {
            setIsAdvancing(true);
            setCurrentStep(prev => prev + 1);
            // Reset advancing flag after step change is processed
            setTimeout(() => setIsAdvancing(false), 500);
        } else {
            // Final step - save and complete
            await handleComplete();
        }
    };
    
    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleComplete = async () => {
        // Prepare data for API and callback
        const profileData = {
            language: formData.language,
            name: formData.name,
            birthday: formData.birthday || null,
            city: formData.city,
            role_type: 'CA',
            ca_cs_status: formData.ca_cs_status || null,
            organisation_name: formData.organisation_name || null,
            qualification: formData.qualification || null,
            coaching_institute: formData.coaching_institute || null,
            articleship_location: formData.articleship_location || null,
            total_experience_years: formData.total_experience_years,
            current_role: formData.current_role || null,
            goals: formData.goals,
        };

        // Immediately complete - CharacterSelection handles the 10s loading screen
        onComplete(profileData);
    };

    const updateFormData = (key, value) => {
        setFormData(prev => {
            const next = { ...prev, [key]: value };
            if (key === 'ca_cs_status') {
                if (!CA_COURSE_LEVELS_WITH_COACHING.includes(value)) {
                    next.coaching_institute = '';
                }

                if (!CA_COURSE_LEVELS_WITH_ARTICLESHIP_LOCATION.includes(value)) {
                    next.articleship_location = '';
                }

                if (!CA_COURSE_LEVELS_WITH_WORKPLACE.includes(value)) {
                    next.organisation_name = '';
                    next.total_experience_years = null;
                }
            }
            return next;
        });
    };

    const toggleGoal = (goalId) => {
        setFormData(prev => {
            const goals = prev.goals.includes(goalId)
                ? prev.goals.filter(g => g !== goalId)
                : [...prev.goals, goalId];
            return { ...prev, goals };
        });
    };

    // Voice recognition - match spoken words to options
    const matchVoiceToOption = useCallback((transcript, stepConfig) => {
        const spokenText = transcript.toLowerCase().trim();
        
        switch (stepConfig.key) {
            case 'language': {
                // Match language options with comprehensive keywords
                const languageKeywords = {
                    // English
                    'english': 'english', 'angrezi': 'english', 'अंग्रेजी': 'english', 'इंग्लिश': 'english',
                    // Hindi
                    'hindi': 'hindi', 'हिंदी': 'hindi', 'हिन्दी': 'hindi',
                    // Tamil
                    'tamil': 'tamil', 'தமிழ்': 'tamil', 'तमिल': 'tamil', 'तामिल': 'tamil',
                    // Telugu
                    'telugu': 'telugu', 'తెలుగు': 'telugu', 'तेलुगु': 'telugu',
                    // Kannada
                    'kannada': 'kannada', 'ಕನ್ನಡ': 'kannada', 'कन्नड़': 'kannada', 'कन्नडा': 'kannada',
                    // Malayalam
                    'malayalam': 'malayalam', 'മലയാളം': 'malayalam', 'मलयालम': 'malayalam',
                    // Marathi
                    'marathi': 'marathi', 'मराठी': 'marathi',
                    // Gujarati
                    'gujarati': 'gujarati', 'ગુજરાતી': 'gujarati', 'गुजराती': 'gujarati',
                    // Bengali
                    'bengali': 'bengali', 'bangla': 'bengali', 'বাংলা': 'bengali', 
                    'बंगाली': 'bengali', 'बांग्ला': 'bengali',
                    // Punjabi
                    'punjabi': 'punjabi', 'ਪੰਜਾਬੀ': 'punjabi', 'पंजाबी': 'punjabi',
                    // Odia
                    'odia': 'odia', 'oriya': 'odia', 'ଓଡ଼ିଆ': 'odia', 
                    'ओड़िया': 'odia', 'उड़िया': 'odia',
                    // Assamese
                    'assamese': 'assamese', 'অসমীয়া': 'assamese', 'असमिया': 'assamese', 'असामी': 'assamese'
                };
                
                for (const [keyword, value] of Object.entries(languageKeywords)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'select', value };
                    }
                }
                break;
            }
            
            case 'name':
            case 'current_role':
            case 'organisation_name':
                // For text inputs, just use the spoken text
                return { type: 'text', value: transcript.trim() };
            
            case 'ca_cs_status': {
                const statusKeywords = {
                    'foundation': 'foundation', 'foundation level': 'foundation', 'foundation ka': 'foundation',
                    'foundation ke': 'foundation', 'फाउंडेशन': 'foundation', 'foundation exam': 'foundation',
                    'intermediate': 'intermediate', 'int': 'intermediate', 'intermediate level': 'intermediate',
                    'inter': 'intermediate', 'इंटरमीडिएट': 'intermediate',
                    'articleship': 'articleship', 'article ship': 'articleship', 'articleship period': 'articleship',
                    'articleship ke': 'articleship', 'articleship kar raha': 'articleship',
                    'finals': 'finals', 'final': 'finals', 'final exam': 'finals', 'फाइनल': 'finals',
                    'clear kar': 'cleared', 'clear kar liya': 'cleared', 'clear ho gaya': 'cleared', 'cleared': 'cleared',
                    'complete': 'cleared', 'passed': 'cleared', 'pass': 'cleared', 'क्लियर': 'cleared',
                    'clear': 'cleared', 'पार': 'cleared', 'ho gaya': 'cleared'
                };
                for (const [keyword, value] of Object.entries(statusKeywords)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'select', value };
                    }
                }
                break;
            }
            case 'city': {
                // City name mappings including Hindi/alternate spellings
                const cityMappings = {
                    'jaipur': 'Jaipur', 'जयपुर': 'Jaipur',
                    'ahmedabad': 'Ahmedabad', 'अहमदाबाद': 'Ahmedabad', 'amdavad': 'Ahmedabad',
                    'lucknow': 'Lucknow', 'लखनऊ': 'Lucknow',
                    'surat': 'Surat', 'सूरत': 'Surat',
                    'indore': 'Indore', 'इंदौर': 'Indore',
                    'chandigarh': 'Chandigarh', 'चंडीगढ़': 'Chandigarh',
                    'pune': 'Pune', 'पुणे': 'Pune', 'poona': 'Pune',
                    'nagpur': 'Nagpur', 'नागपुर': 'Nagpur',
                    'vadodara': 'Vadodara', 'वडोदरा': 'Vadodara', 'baroda': 'Vadodara',
                    'bhopal': 'Bhopal', 'भोपाल': 'Bhopal',
                    'coimbatore': 'Coimbatore', 'कोयंबटूर': 'Coimbatore',
                    'kochi': 'Kochi', 'कोच्चि': 'Kochi', 'cochin': 'Kochi',
                    'vizag': 'Vizag', 'visakhapatnam': 'Vizag', 'विशाखापट्टनम': 'Vizag',
                    'rajkot': 'Rajkot', 'राजकोट': 'Rajkot',
                    'other': 'Other', 'अन्य': 'Other', 'दूसरा': 'Other'
                };
                
                // Check direct mapping first
                for (const [keyword, city] of Object.entries(cityMappings)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'select', value: city };
                    }
                }
                
                // Fallback to fuzzy match on options
                const options = stepConfig.options;
                let match = options.find(opt => 
                    spokenText.includes(opt.toLowerCase()) || 
                    opt.toLowerCase().includes(spokenText)
                );
                if (match) return { type: 'select', value: match };
                break;
            }

            case 'birthday': {
                // Return async marker - will be handled separately with LLM
                return { type: 'async_date', value: spokenText, isFutureDate: false };
            }
            
            case 'qualification': {
                // Qualification mappings including Hindi/alternate spellings and phonetic variations
                const qualificationMappings = {
                    // B.Com - many ways people say it
                    'b.com': 'B.Com', 'bcom': 'B.Com', 'b com': 'B.Com', 
                    'b. com': 'B.Com', 'be com': 'B.Com', 'bee com': 'B.Com',
                    'b calm': 'B.Com', 'be calm': 'B.Com', 'become': 'B.Com',
                    'bachelor of commerce': 'B.Com', 'बीकॉम': 'B.Com', 'बी कॉम': 'B.Com',
                    'b column': 'B.Com', 'b common': 'B.Com',
                    // M.Com
                    'm.com': 'M.Com', 'mcom': 'M.Com', 'm com': 'M.Com',
                    'm. com': 'M.Com', 'em com': 'M.Com', 'em calm': 'M.Com',
                    'master of commerce': 'M.Com', 'एमकॉम': 'M.Com', 'एम कॉम': 'M.Com',
                    'm column': 'M.Com', 'm common': 'M.Com',
                    // CA Inter
                    'ca inter': 'CA Inter', 'ca intermediate': 'CA Inter', 
                    'c a inter': 'CA Inter', 'see a inter': 'CA Inter',
                    'ca enter': 'CA Inter', 'ca intra': 'CA Inter',
                    'सीए इंटर': 'CA Inter', 'ipcc': 'CA Inter', 'ipc': 'CA Inter',
                    // CA
                    'ca final': 'CA', 'chartered accountant': 'CA', 
                    'c a': 'CA', 'see a': 'CA', 'ca complete': 'CA',
                    'सीए': 'CA', 'चार्टर्ड अकाउंटेंट': 'CA',
                    'c.a': 'CA', 'c.a.': 'CA',
                    // CMA Inter
                    'cma inter': 'CMA Inter', 'cma intermediate': 'CMA Inter',
                    'c m a inter': 'CMA Inter', 'see m a inter': 'CMA Inter',
                    'icwa inter': 'CMA Inter', 'सीएमए इंटर': 'CMA Inter',
                    // CMA
                    'cma final': 'CMA', 'cost accountant': 'CMA', 'cma complete': 'CMA',
                    'c m a': 'CMA', 'see m a': 'CMA', 'c.m.a': 'CMA',
                    'icwa': 'CMA', 'सीएमए': 'CMA',
                    // CS Inter
                    'cs inter': 'CS Inter', 'cs intermediate': 'CS Inter',
                    'c s inter': 'CS Inter', 'see s inter': 'CS Inter',
                    'company secretary inter': 'CS Inter', 'सीएस इंटर': 'CS Inter',
                    // CS
                    'cs final': 'CS', 'company secretary': 'CS', 'cs complete': 'CS',
                    'c s': 'CS', 'see s': 'CS', 'c.s': 'CS', 'c.s.': 'CS',
                    'सीएस': 'CS', 'कंपनी सेक्रेटरी': 'CS'
                };
                
                // Check direct mapping first
                for (const [keyword, qual] of Object.entries(qualificationMappings)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'select', value: qual };
                    }
                }
                
                // Fallback to fuzzy match on options
                const options = stepConfig.options;
                let match = options.find(opt => 
                    spokenText.includes(opt.toLowerCase()) || 
                    opt.toLowerCase().includes(spokenText)
                );
                if (match) return { type: 'select', value: match };
                break;
            }
            
            case 'experience': {
                // Match experience options with both English and Hindi keywords
                const experienceKeywords = {
                    // Fresher (0 years)
                    'fresher': 0, 'fresh': 0, 'freshar': 0, 'pressure': 0,
                    'abhi shuru': 0, 'naya': 0, 'new': 0, 'no experience': 0,
                    'फ्रेशर': 0, 'नया': 0, 'अभी शुरू': 0, 'कोई नहीं': 0, 
                    'zero': 0, 'शून्य': 0, 'just started': 0, 'none': 0,
                    'koi nahi': 0, 'nahi hai': 0, 'abhi start': 0,
                    // 1 year
                    '1 year': 1, 'one year': 1, '1 saal': 1, 'ek saal': 1,
                    'एक साल': 1, '१ साल': 1, 'single year': 1,
                    '1year': 1, 'oneyear': 1, 'ek sal': 1, 'eksaal': 1,
                    // 2 years
                    '2 year': 2, 'two year': 2, '2 saal': 2, 'do saal': 2,
                    'दो साल': 2, '२ साल': 2, '2year': 2, 'twoyear': 2,
                    '2 sal': 2, 'do sal': 2, 'dosaal': 2, 'to year': 2, 'too year': 2,
                    // 3 years
                    '3 year': 3, 'three year': 3, '3 saal': 3, 'teen saal': 3,
                    'तीन साल': 3, '३ साल': 3, '3year': 3, 'threeyear': 3,
                    '3 sal': 3, '3sal': 3, 'tin saal': 3, 'teen sal': 3, 'tree year': 3,
                    // 4-5 years
                    '4 year': 4.5, '5 year': 4.5, 'four year': 4.5, 'five year': 4.5, 
                    '4 saal': 4.5, '5 saal': 4.5, 'char saal': 4.5, 'panch saal': 4.5,
                    'चार साल': 4.5, 'पांच साल': 4.5, '४ साल': 4.5, '५ साल': 4.5,
                    '4-5': 4.5, 'four five': 4.5, '4year': 4.5, '5year': 4.5,
                    'for year': 4.5, 'paanch': 4.5, 'char sal': 4.5, 'panch sal': 4.5,
                    // 6-10 years
                    '6 year': 8, '7 year': 8, '8 year': 8, '9 year': 8, '10 year': 8,
                    '6 saal': 8, '7 saal': 8, '8 saal': 8, '9 saal': 8, '10 saal': 8,
                    'छह साल': 8, 'सात साल': 8, 'आठ साल': 8, 'नौ साल': 8, 'दस साल': 8,
                    '६ साल': 8, '७ साल': 8, '८ साल': 8, '९ साल': 8, '१० साल': 8,
                    'six year': 8, 'seven year': 8, 'eight year': 8, 'nine year': 8,
                    '6-10': 8, 'chhe saal': 8, 'saat saal': 8, 'aath saal': 8, 'nau saal': 8,
                    '6year': 8, '7year': 8, '8year': 8, '9year': 8, '10year': 8,
                    'chhah': 8, 'saat': 8, 'aath': 8, 'nau': 8, 'das': 8,
                    // 10+ years
                    '10+': 10, 'ten plus': 10, 'das saal': 10, 'bahut': 10,
                    'दस से ज्यादा': 10, '१०+': 10, 'ten year': 10, 'more than 10': 10,
                    'gyarah': 10, 'barah': 10, 'ग्यारह': 10, 'बारह': 10,
                    '11': 10, '12': 10, '15': 10, '20': 10, 'plus': 10,
                    '10 plus': 10, 'eleven': 10, 'twelve': 10, 'fifteen': 10,
                    'das se zyada': 10, 'kaafi': 10
                };
                
                for (const [keyword, value] of Object.entries(experienceKeywords)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'experience', value };
                    }
                }
                break;
            }
            
            case 'goals': {
                // Match goal options with comprehensive keywords
                const goalKeywords = {
                    // Salary / Increase salary
                    'salary': 'salary', 'paise': 'salary', 'money': 'salary', 'increment': 'salary',
                    'सैलरी': 'salary', 'पैसे': 'salary', 'पैसा': 'salary', 'तनख्वाह': 'salary',
                    'income': 'salary', 'earning': 'salary', 'kamai': 'salary', 'कमाई': 'salary',
                    'badhana': 'salary', 'increase': 'salary', 'hike': 'salary', 'raise': 'salary',
                    
                    // Clear exams
                    'exam': 'exams', 'exams': 'exams', 'study': 'exams', 'padhai': 'exams', 'clear': 'exams',
                    'एग्जाम': 'exams', 'परीक्षा': 'exams', 'पढ़ाई': 'exams', 'पास': 'exams',
                    'pass': 'exams', 'qualify': 'exams', 'pariksha': 'exams',
                    
                    // Switch role/company
                    'switch': 'switch', 'change': 'switch', 'company': 'switch', 'role': 'switch', 'job': 'switch',
                    'कंपनी': 'switch', 'बदलना': 'switch', 'नौकरी': 'switch', 'जॉब': 'switch',
                    'badalna': 'switch', 'naukri': 'switch', 'transfer': 'switch', 'move': 'switch',
                    
                    // Build network
                    'network': 'network', 'connection': 'network', 'log se milna': 'network',
                    'नेटवर्क': 'network', 'कनेक्शन': 'network', 'लोगों से मिलना': 'network',
                    'connect': 'network', 'people': 'network', 'contacts': 'network', 'networking': 'network',
                    'log': 'network', 'लोग': 'network',
                    
                    // Learn new skills
                    'skill': 'skills', 'skills': 'skills', 'learn': 'skills', 'seekhna': 'skills',
                    'स्किल': 'skills', 'सीखना': 'skills', 'नया सीखना': 'skills',
                    'training': 'skills', 'course': 'skills', 'knowledge': 'skills', 'gyan': 'skills',
                    'ज्ञान': 'skills', 'कोर्स': 'skills',
                    
                    // Start own practice/business
                    'business': 'business', 'practice': 'business', 'apna': 'business', 'start': 'business',
                    'बिजनेस': 'business', 'व्यापार': 'business', 'अपना': 'business', 'खुद का': 'business',
                    'own': 'business', 'entrepreneur': 'business', 'startup': 'business', 'firm': 'business',
                    'प्रैक्टिस': 'business', 'फर्म': 'business', 'khud ka': 'business'
                };
                
                for (const [keyword, goalId] of Object.entries(goalKeywords)) {
                    if (spokenText.includes(keyword)) {
                        return { type: 'goal', value: goalId };
                    }
                }
                break;
            }
            
            default:
                break;
        }
        
        return null;
    }, []);

    // Internal start listening (used by auto-listen)
    const startListeningInternal = useCallback(() => {
        if (!SpeechRecognition || isListening || isAdvancing) return;
        
        // Capture the step at the time of starting
        const capturedStep = currentStepRef.current;
        
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-IN';
        recognition.continuous = false;
        recognition.interimResults = true; // Show interim results
        recognition.maxAlternatives = 5;
        
        recognition.onstart = () => {
            setIsListening(true);
            setHeardText('');
        };
        
        recognition.onresult = (event) => {
            // Check if step has changed since we started listening
            if (currentStepRef.current !== capturedStep) {
                console.log('Step changed, ignoring result');
                return;
            }
            
            const result = event.results[event.results.length - 1];
            let transcript = result[0].transcript;
            
            // Show what's being heard in real-time
            setHeardText(transcript);
            
            // Only process final results
            if (!result.isFinal) return;
            
            const stepConfig = STEPS[capturedStep];
            
            console.log('Voice heard:', transcript, 'for step:', stepConfig.key);
            
            let match = matchVoiceToOption(transcript, stepConfig);
            
            // If no match with primary transcript, try alternatives
            if (!match && result.length > 1) {
                for (let i = 1; i < result.length; i++) {
                    const altTranscript = result[i].transcript;
                    console.log('Trying alternative:', altTranscript);
                    match = matchVoiceToOption(altTranscript, stepConfig);
                    if (match) {
                        transcript = altTranscript;
                        break;
                    }
                }
            }
            
            if (match) {
                console.log('Match found:', match);
                let shouldAutoAdvance = false;
                
                switch (match.type) {
                    case 'text':
                        updateFormData(stepConfig.key, match.value);
                        shouldAutoAdvance = true;
                        break;
                    case 'select':
                        updateFormData(stepConfig.key, match.value);
                        shouldAutoAdvance = true;
                        break;
                    case 'experience':
                        updateFormData('total_experience_years', match.value);
                        shouldAutoAdvance = true;
                        break;
                    case 'async_date':
                        // Handle async LLM date parsing
                        recognition.stop();
                        setHeardText(`Parsing: "${match.value}"...`);
                        (async () => {
                            try {
                                const parsedDate = await parseDateWithLLM(match.value, match.isFutureDate);
                                if (parsedDate && currentStepRef.current === capturedStep) {
                                    updateFormData(stepConfig.key, parsedDate);
                                    setHeardText(`Parsed: ${formatBirthdayDisplay(parsedDate)}`);
                                    setTimeout(() => {
                                        if (currentStepRef.current === capturedStep) {
                                            handleNext();
                                        }
                                    }, 600);
                                } else {
                                    setHeardText(`Could not parse date from: "${match.value}"`);
                                    // Restart listening to try again
                                    setTimeout(() => {
                                        if (autoListenEnabled && currentStepRef.current === capturedStep) {
                                            startListeningInternal();
                                        }
                                    }, 1500);
                                }
                            } catch (error) {
                                console.error('LLM date parsing error:', error);
                                setHeardText(`Error parsing date. Please try again.`);
                                setTimeout(() => {
                                    if (autoListenEnabled && currentStepRef.current === capturedStep) {
                                        startListeningInternal();
                                    }
                                }, 1500);
                            }
                        })();
                        return; // Don't continue with normal flow
                    case 'goal':
                        if (!formData.goals.includes(match.value)) {
                            toggleGoal(match.value);
                        }
                        // Don't auto-advance for goals (multiselect)
                        // But restart listening for more goals
                        setTimeout(() => {
                            if (autoListenEnabled && currentStepRef.current === capturedStep) {
                                startListeningInternal();
                            }
                        }, 1000);
                        break;
                    default:
                        break;
                }
                
                // Auto-advance after a short delay (except for goals)
                if (shouldAutoAdvance && stepConfig.key !== 'goals') {
                    // Stop recognition first
                    recognition.stop();
                    setTimeout(() => {
                        if (currentStepRef.current === capturedStep) {
                            handleNext();
                        }
                    }, 600);
                }
            } else {
                console.log('No match found for:', transcript);
                const normalizedTranscript = transcript.trim();
                if (stepConfig.key === 'city' && normalizedTranscript) {
                    setCityOptionsState((prev) => {
                        const alreadyExists = prev.some(
                            (option) => option.toLowerCase() === normalizedTranscript.toLowerCase()
                        );
                        return alreadyExists ? prev : [...prev, normalizedTranscript];
                    });
                    updateFormData('city', normalizedTranscript);
                    recognition.stop();
                    setTimeout(() => {
                        if (currentStepRef.current === capturedStep) {
                            handleNext();
                        }
                    }, 600);
                    return;
                }

                // For text inputs, use the raw transcript and auto-advance
                if (stepConfig.type === 'text' && normalizedTranscript) {
                    updateFormData(stepConfig.key, normalizedTranscript);
                    recognition.stop();
                    setTimeout(() => {
                        if (currentStepRef.current === capturedStep) {
                            handleNext();
                        }
                    }, 600);
                } else {
                    setTimeout(() => {
                        if (autoListenEnabled && currentStepRef.current === capturedStep) {
                            startListeningInternal();
                        }
                    }, 500);
                }
            }
        };
        
        recognition.onerror = (event) => {
            console.log('Speech recognition error:', event.error);
            setIsListening(false);
            setHeardText('');
            // Restart listening on error (except for aborted or no-speech)
            if (event.error !== 'aborted' && event.error !== 'no-speech' && autoListenEnabled) {
                setTimeout(() => {
                    if (currentStepRef.current === capturedStep) {
                        startListeningInternal();
                    }
                }, 1000);
            }
        };
        
        recognition.onend = () => {
            setIsListening(false);
        };
        
        recognitionRef.current = recognition;
        recognition.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isListening, isAdvancing, matchVoiceToOption, formData.goals, autoListenEnabled]);

    // Keep the ref updated so speak() can call it
    useEffect(() => {
        startListeningRef.current = startListeningInternal;
    }, [startListeningInternal]);

    // Manual start listening (stops audio first)
    const startListening = useCallback(() => {
        stopAllAudio();
        if (autoListenTimeoutRef.current) {
            clearTimeout(autoListenTimeoutRef.current);
        }
        startListeningInternal();
    }, [stopAllAudio, startListeningInternal]);

    // Stop listening
    const stopListening = useCallback(() => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        if (autoListenTimeoutRef.current) {
            clearTimeout(autoListenTimeoutRef.current);
        }
        setIsListening(false);
        setHeardText('');
    }, []);

    // Cleanup recognition on unmount or step change
    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (autoListenTimeoutRef.current) {
                clearTimeout(autoListenTimeoutRef.current);
            }
        };
    }, [currentStep]);

    // Render different input types
    const renderInput = () => {
        const step = STEPS[currentStep];
        
        switch (step.type) {
            case 'language':
                return (
                    <div className="language-grid">
                        {LANGUAGE_OPTIONS.map((lang) => (
                            <button
                                key={lang.value}
                                className={`language-card ${formData.language === lang.value ? 'selected' : ''}`}
                                onClick={() => updateFormData('language', lang.value)}
                            >
                                <span className="language-native">{lang.native}</span>
                                <span className="language-label">{lang.label}</span>
                            </button>
                        ))}
                    </div>
                );
            
            case 'text':
                return (
                    <div className="input-container">
                        <input
                            type="text"
                            className="onboarding-text-input"
                            value={formData[step.key]}
                            onChange={(e) => updateFormData(step.key, e.target.value)}
                            placeholder={
                                step.key === 'name'
                                    ? 'Apna naam likhein'
                                    : 'Type karein...'
                            }
                            autoFocus
                            autoComplete="off"
                        />
                        {!step.required && (
                            <button 
                                className="skip-btn"
                                onClick={() => {
                                    updateFormData(step.key, '');
                                    handleNext();
                                }}
                            >
                                Skip karein →
                            </button>
                        )}
                    </div>
                );

            case 'birthday':
                const today = new Date().toISOString().split('T')[0];
                return (
                    <div className="birthday-input-group">
                        <input
                            type="date"
                            className="birthday-date-input"
                            value={formData[step.key]}
                            onChange={(e) => updateFormData(step.key, e.target.value)}
                            min="1900-01-01"
                            max={today}
                        />
                        <p className="birthday-helper-text">
                            {formData[step.key]
                                ? `Selected: ${formatBirthdayDisplay(formData[step.key])}`
                                : 'Calendar se select karein ya bol ke bataein'
                            }
                        </p>
                    </div>
                );
            
            case 'ca_cs_status':
                return (
                    <div className="status-grid">
                        {CA_CS_STATUS_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                className={`status-card ${formData.ca_cs_status === option.value ? 'selected' : ''}`}
                                onClick={() => updateFormData('ca_cs_status', option.value)}
                            >
                                <span className="status-label">{option.label}</span>
                                <span className="status-sublabel">{option.sublabel}</span>
                            </button>
                        ))}
                    </div>
                );
            
            case 'chips':
                return (
                    <div className="chips-container">
                        {step.options.map((option) => (
                            <button
                                key={option}
                                className={`chip ${formData[step.key] === option ? 'selected' : ''}`}
                                onClick={() => updateFormData(step.key, option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                );
            
            case 'experience':
                return (
                    <div className="experience-grid">
                        {EXPERIENCE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                className={`experience-card ${formData.total_experience_years === option.value ? 'selected' : ''}`}
                                onClick={() => updateFormData('total_experience_years', option.value)}
                            >
                                <span className="experience-label">{option.label}</span>
                                <span className="experience-sublabel">{option.sublabel}</span>
                            </button>
                        ))}
                    </div>
                );
            
            case 'multiselect':
                return (
                    <div className="goals-grid">
                        {GOAL_OPTIONS.map((goal) => (
                            <button
                                key={goal.id}
                                className={`goal-card ${formData.goals.includes(goal.id) ? 'selected' : ''}`}
                                onClick={() => toggleGoal(goal.id)}
                            >
                                <span className="goal-emoji">{goal.emoji}</span>
                                <span className="goal-label">{goal.label}</span>
                                <span className="goal-sublabel">{goal.sublabel}</span>
                                {formData.goals.includes(goal.id) && (
                                    <span className="goal-check">✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <div className="onboarding-container">
            {/* Header with audio controls */}
            <div className="onboarding-header">
                <AudioControls onReplay={handleReplay} isPlaying={isPlaying} />
            </div>

            {/* Main content */}
            <div className="onboarding-content">
                {/* Back button and progress */}
                <div className="navigation-row">
                    {currentStep > 0 ? (
                        <button className="back-btn" onClick={handleBack}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                        </button>
                    ) : (
                        <div className="back-btn-placeholder" />
                    )}
                    <ProgressDots current={currentStep} total={totalSteps} />
                    <div className="back-btn-placeholder" />
                </div>

                {/* Question */}
                <h1 className="onboarding-question">
                    {NARRATIONS[STEPS[currentStep].key]}
                </h1>

                {/* Show listening indicator only (no transcript) */}
                {isListening && (
                    <div className="heard-text-container">
                        <div className="listening-indicator">
                            <div className="listening-pulse"></div>
                            <span>Sun raha hoon...</span>
                        </div>
                    </div>
                )}

                {/* Input area */}
                <div className="onboarding-input-area">
                    {renderInput()}
                </div>
            </div>

            {/* Voice controls - outside scrollable area */}
            {voiceSupported && (
                <div className="voice-controls-container">
                    {/* Voice mode toggle */}
                    <button 
                        className={`voice-mode-toggle ${autoListenEnabled ? 'active' : ''}`}
                        onClick={() => setAutoListenEnabled(!autoListenEnabled)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        </svg>
                        <span>{autoListenEnabled ? 'Voice Mode ON' : 'Voice Mode OFF'}</span>
                    </button>
                    
                    {/* Manual mic button (when not auto-listening) */}
                    {!isListening && !isPlaying && (
                        <button 
                            className="tap-to-speak-btn"
                            onClick={startListening}
                        >
                            Tap to speak again
                        </button>
                    )}
                    
                    {/* Stop button when listening */}
                    {isListening && (
                        <button 
                            className="stop-listening-btn"
                            onClick={stopListening}
                        >
                            ✕ Stop
                        </button>
                    )}
                </div>
            )}

            {/* Footer with CTA */}
            <div className="onboarding-footer">
                <button
                    className={`continue-btn ${isStepValid() ? 'enabled' : 'disabled'}`}
                    onClick={handleNext}
                    disabled={!isStepValid() && STEPS[currentStep].required}
                >
                    {currentStep === totalSteps - 1 ? 'Shuru Karein →' : 'Aage Badho →'}
                </button>
            </div>
        </div>
    );
};

export default Onboarding;

